import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Alert,
  Paper,
  Grid,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LaunchIcon from "@mui/icons-material/Launch";
import LinkIcon from "@mui/icons-material/Link";
import {
  EventDateView,
  EventStatusView,
  SoldText,
  EventTitle,
  ViewEventButton,
} from "./components";
import {
  fetchTicketsForEvent,
  fetchOrdersForEvent,
  fetchSessionsForEvent,
} from "../../../../redux/features/adminEventsSlice";

const USDollar = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const formatSessionRevenue = (sessionOrders) => {
  const totalUsdCents = sessionOrders.reduce(
    (total, order) => total + (order.totalUsdCents || 0),
    0,
  );
  return USDollar.format(totalUsdCents / 100);
};

const formatSessionTicketCount = (sessionOrders) => {
  return sessionOrders.reduce((total, order) => {
    const orderItems = order.items || [];
    const orderTicketCount = orderItems.reduce(
      (itemTotal, item) => itemTotal + (item.quantity || 0),
      0,
    );
    return total + orderTicketCount;
  }, 0);
};

const EventListItem = ({ event, handleClick }) => {
  const dispatch = useDispatch();
  const [showCopySnackbar, setShowCopySnackbar] = useState(false);
  const [sessionsExpanded, setSessionsExpanded] = useState(false);
  const [sessionMetricsRequested, setSessionMetricsRequested] = useState(false);
  const [sessionsRequested, setSessionsRequested] = useState(false);
  const onShowCopyToast = () => {
    setShowCopySnackbar(true);
  };
  const sessions = event?.sessions || [];
  const sessionsCount = event?.sessionsCount ?? 0;
  const hasSessions = sessionsCount > 0;

  const ordersBySessionId = useMemo(() => {
    const orders = event?.orders || [];
    return orders.reduce((acc, order) => {
      if (!order.sessionId) {
        return acc;
      }
      return {
        ...acc,
        [order.sessionId]: [...(acc[order.sessionId] || []), order],
      };
    }, {});
  }, [event?.orders]);

  const handleSessionAccordionChange = async (_, expanded) => {
    setSessionsExpanded(expanded);
    if (!expanded) {
      return;
    }
    const requests = [];
    if (!sessionsRequested) {
      setSessionsRequested(true);
      requests.push(dispatch(fetchSessionsForEvent({ eventId: event.id })));
    }
    if (!sessionMetricsRequested) {
      setSessionMetricsRequested(true);
      requests.push(dispatch(fetchTicketsForEvent({ eventId: event.id })));
      requests.push(
        dispatch(
          fetchOrdersForEvent({
            eventId: event.id,
            organizationId: event.organizationId,
          }),
        ),
      );
    }
    if (requests.length > 0) {
      await Promise.all(requests);
    }
  };

  return (
    <Paper elevation={5}>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={showCopySnackbar}
        autoHideDuration={2000}
        onClose={async () => {
          await setShowCopySnackbar(false);
        }}
      >
        <Alert
          onClose={async () => {
            await setShowCopySnackbar(false);
          }}
          severity="success"
        >
          URL Copied!
        </Alert>
      </Snackbar>
      <Grid
        container
        sx={{
          cursor: "pointer",
          p: "20px",
          alignItems: "center",
          // When the sessions accordion follows, avoid stacking full grid padding
          // with the accordion’s own top edge so the divider doesn’t sit in a deep “well”.
          pb: hasSessions ? "12px" : "20px",
        }}
        spacing={1}
        onClick={() => {
          handleClick(event.id);
        }}
      >
        <Grid item xs={12} sm={12} md={1}>
          <EventDateView event={event} />
        </Grid>

        <Grid item xs={12} sm={12} md={7}>
          <EventTitle event={event} />
        </Grid>
        <Grid item xs={10} sm={10} md={1}>
          <SoldText {...event} />
        </Grid>
        <Grid item xs={2} sm={2} md={2}>
          <ViewEventButton event={event} onShowCopyToast={onShowCopyToast} />
        </Grid>
        <Grid item xs={12} sm={12} md={1} sx={{ paddingTop: "0px !important" }}>
          <EventStatusView {...event} />
        </Grid>
      </Grid>
      {hasSessions && (
        <Accordion
          expanded={sessionsExpanded}
          onChange={handleSessionAccordionChange}
          disableGutters
          elevation={0}
          sx={{
            px: "20px",
            pt: 0,
            // Keep padding off the accordion root so the summary row is the only “strip”
            // (extra pb here looked like a tall row with text stuck to the top).
            pb: 0,
            // Single clear divider; MUI’s default :before sits oddly with our layout spacing.
            "&:before": { display: "none" },
            borderTop: 1,
            borderColor: "divider",
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            onClick={(e) => {
              e.stopPropagation();
            }}
            sx={{
              px: 0,
              py: 1.5,
              minHeight: 48,
              display: "flex",
              alignItems: "center",
              "& .MuiAccordionSummary-content": {
                flexGrow: 1,
                m: 0,
                my: 0,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiAccordionSummary-expandIconWrapper": {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                alignSelf: "center",
              },
            }}
          >
            <Typography
              component="span"
              color="primary"
              sx={{ m: 0, lineHeight: 1.2 }}
            >{`${sessionsCount} Sessions`}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
            <Box display="flex" flexDirection="column" gap="8px">
              {sessions.map((session) => {
                const sessionOrders = ordersBySessionId[session.id] || [];
                const purchasedFromSessionCount =
                  formatSessionTicketCount(sessionOrders);
                return (
                  <Box
                    key={`event-${event.id}-session-${session.id}`}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    padding="8px 0"
                    borderTop="1px solid"
                    borderColor="divider"
                  >
                    <Box>
                      <Typography variant="body2">{session.name || "Unnamed session"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`Can attend: ${session.totalTickets || 0}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {`Purchased via session page: ${purchasedFromSessionCount} (${formatSessionRevenue(sessionOrders)})`}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap="4px">
                      <Tooltip title="Open session page">
                        <span>
                          <IconButton
                            disabled={!session.publicUrl}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!session.publicUrl) {
                                return;
                              }
                              window.open(session.publicUrl, "_blank", "noreferrer");
                            }}
                          >
                            <LaunchIcon color={session.publicUrl ? "primary" : "disabled"} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Copy session URL">
                        <span>
                          <IconButton
                            disabled={!session.publicUrl}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!session.publicUrl) {
                                return;
                              }
                              navigator.clipboard.writeText(session.publicUrl);
                              onShowCopyToast();
                            }}
                          >
                            <LinkIcon color={session.publicUrl ? "primary" : "disabled"} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
};

export default EventListItem;
